import React, { Fragment, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { customerApi } from '@app/store';
import { ICustomer, CustomerDTO } from '@app/common';
import { Button, Form, Panel, CardButtonActionMode } from '@app/components';
import { CustomerForm } from './CustomerForm';
import { CustomerFormValues } from './customer-form.helpers';
import { useToast, useTriptych } from '@app/hooks';
import { CustomerCard } from './CustomerCard';
import { CheckPermissions } from '@app/components/CheckPermissions';

const transformToUpdatableFormCustomer = (customer?: Partial<ICustomer>): CustomerFormValues => {
  return {
    firstname: customer?.firstname || '',
    lastname: customer?.lastname || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    birthday: customer?.birthday ? new Date(customer.birthday).toISOString().split('T')[0] : '',
    addresses: customer?.addresses || [],
    optInEmail: Boolean(customer?.optInEmail) || false,
    optInSMS: Boolean(customer?.optInSMS) || false,
    optInLoyalty: Boolean(customer?.optInLoyalty) || false,
  }
}

interface CustomerUpdateProps {
  customer?: Partial<ICustomer>;
  isFetching: boolean;
  onDelete: (customer: ICustomer) => void;
  toggle: (mode: CardButtonActionMode) => void;
}

export const CustomerUpdate = ({ customer, isFetching, onDelete, toggle }: CustomerUpdateProps) => {
  const toast = useToast();

  const triptych = useTriptych()

  const [update, { isLoading, error: apiErrors }] = customerApi.useUpdateOneCustomersMutation();

  const formMethods = useForm<CustomerFormValues>({
    resolver: classValidatorResolver(CustomerDTO),
    defaultValues: transformToUpdatableFormCustomer(customer),
  });

  useEffect(() => {
    formMethods.reset(transformToUpdatableFormCustomer(customer));
  }, [customer, formMethods.reset]);

  const onUpdate = async (formValues: CustomerFormValues) => {
    try {
      const updatedCustomer = await update({ _id: customer?._id, ...formValues }).unwrap();

      triptych.close({ mode: 'UPDATE', data: updatedCustomer });

      toast.success('Modification effectuée avec succès.')
    } catch {
      toast.error('Une erreur est survenue lors de la modification.')
    }
  }

  return (
    <Fragment>
      <CustomerCard
        customer={customer}
        formMethods={formMethods}
        toggle={toggle} />

      <Panel.Content>

        <Form isLoading={isFetching} formMethods={formMethods} apiErrors={apiErrors}>

          <Panel.FormContainer>

            <CheckPermissions permissions={['dashboard::customers.delete']}>
              <Panel.Header>
                <Button
                  title="Supprimer"
                  onPress={() => onDelete(customer as ICustomer)}
                  bordered
                  important />
              </Panel.Header>
            </CheckPermissions>

            <CustomerForm />

          </Panel.FormContainer>

          <Panel.Actions>
            <Button
              title="Retour"
              grow
              large
              bordered
              onPress={() => triptych.close()} />

            <Button
              loading={isLoading}
              onPress={formMethods.handleSubmit(onUpdate)}
              title="Valider"
              grow
              large />
          </Panel.Actions>

        </Form>

      </Panel.Content>
    </Fragment>
  );
}; 